/**
 * The portal frame's controller: `shell.model.ts` wired to the three reads
 * that put a number on the rail — `fetchVerificationRequests`,
 * `fetchNotifications`, and one side or the other of the settlements ledger.
 *
 * It owns no navigation state at all — the destination is a URL segment
 * parsed upstream — and what it does own the model cannot: the three badge
 * counts, each re-read as the reader moves between destinations. The model
 * decides everything else about the frame: the nav, the two cards, the
 * heading. Each portal's own destinations and copy live on its `PortalSpec`.
 *
 * The badges count different things on purpose: verifications and
 * settlements count WORK and stay until it is done; notifications count NEWS
 * and clear when looked at. Collapsing them would make one nav item lie.
 */

import { useEffect, useState } from "react";
import type {
  Capabilities,
  MeResponse,
  Membership,
  OrgType,
  RoleKey,
  WorkspaceRef,
} from "@ondo/shared";

import {
  fetchArtistSettlements,
  fetchGallerySettlements,
  fetchNotifications,
  fetchVerificationRequests,
} from "../lib/api.ts";
import {
  awaitingVerificationCount,
  dueSettlementCount,
  inboxPrincipalFor,
  resolvePortal,
  unansweredRemittanceCount,
} from "./shell.model.ts";
import type { PortalSpec } from "./portal.ts";
import type { PortalView } from "./shell.model.ts";

export interface PortalController {
  view: PortalView;
}

/**
 * Navigation inside a portal.
 *
 * The destination is a URL segment, parsed upstream in `resolveWorkspaceRoute`
 * against this portal's own spec and handed in here, so this hook holds no
 * navigation state at all — every link in the shell is an anchor and the back
 * button works.
 *
 * A WORK HAS AN ADDRESS, `…/work/:id`, and an earlier version of this comment
 * argued it should not. The argument was aimed at the wrong thing. A work URL
 * is not a share affordance and never becomes one: it 403s for anyone without a
 * grant, exactly as `…/collection` does, and pasting it to somebody grants them
 * nothing. What it buys is that a collector adding a provenance entry can
 * reload without losing the screen.
 *
 * THE RULE THAT GOES WITH IT: no "copy link" and no "share this work" control
 * ever appears on that screen. Showing one piece to a valuer is a deliberate
 * grant — `POST /collections/:id/grants` — and a link that stood in for it
 * would be this model inverted.
 *
 * The wizards are the other half of it. `…/register/provenance` on a cold load
 * is a Provenance panel over an empty form with no server-side draft, so
 * `useRegisterController` keeps its stage union — as does
 * `useSignupController`. A screen gets an address; a step in a form does not.
 */
export function usePortalController(input: {
  spec: PortalSpec;
  destination: string;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  role: RoleKey;
  roleLabel: string;
  canSwitch: boolean;
  orgKindLabel: (orgType: OrgType) => string;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
}): PortalController {
  const { badges: ids } = input.spec;
  // Scoped to the workspace's own inbox, exactly as the notifications badge
  // below is — the count on the rail and the list behind it must be reading
  // one feed. `my_verification_requests`'s null scope "keeps the whole set,
  // which is what a badge count wants" was written when the badge sat on ONE
  // personal workspace; with firm portals it is the opposite of what a badge
  // wants, because a director with asks of their own would carry the personal
  // count into the studio's rail, over a screen that cannot show those rows.
  const waiting = useAwaitingVerifications(
    ids.verifications !== null && input.capabilities.verifyProvenance,
    inboxPrincipalFor(input.principal, input.workspace),
  );
  // WHOSE inbox the badge counts is the same question the screen answers, and
  // it has to give the same answer: a firm's workspace reads the FIRM's feed.
  // Counting the person's own inbox beside a screen showing the firm's would
  // hang a number on a nav item that the page behind it cannot explain.
  const unseen = useUnseenNotifications(
    ids.notifications !== null,
    input.destination,
    inboxPrincipalFor(input.principal, input.workspace),
  );
  const owed = useOutstandingSettlements(
    (ids.settlements ?? null) !== null,
    input.workspace.role === "gallery",
    input.destination,
  );

  return {
    view: resolvePortal({
      ...input,
      // Keyed by the spec's own ids rather than the literals, so a portal whose
      // rail carries none of these items pays for none of the requests and
      // hangs no count on a destination it does not have.
      badges: {
        ...(ids.verifications && waiting !== null ? { [ids.verifications]: waiting } : {}),
        ...(ids.notifications && unseen ? { [ids.notifications]: unseen } : {}),
        ...(ids.settlements && owed ? { [ids.settlements]: owed } : {}),
      },
    }),
  };
}

/**
 * How many notifications have arrived since the inbox was last opened.
 *
 * UNSEEN AND NOT AWAITING, which is the opposite of the badge below it, and
 * deliberately so: the verifications badge counts outstanding work and stays
 * until it is done, while this one counts news and clears when it is looked at.
 * The two numbers legitimately differ — a request answered from another seat
 * stops being work while still being news — and collapsing them would make one
 * of the two nav items lie.
 *
 * Ungated by a capability, unlike verifications: anyone can be sent something.
 * `GET /v1/notifications` marks nothing seen, which is what lets this run on
 * every page without clearing the count it is reporting.
 *
 * A refusal, an unconfigured feed store and an empty inbox all mean no badge.
 * None is an error worth taking a whole workspace down for.
 */
function useUnseenNotifications(
  enabled: boolean,
  destination: string,
  inboxOndoId: string | null,
): number {
  const [unseen, setUnseen] = useState(0);

  /**
   * Re-read on every move between destinations, which is what `destination` is
   * doing in the dependencies.
   *
   * The shell is rendered by the route rather than remounted per URL — that is
   * what keeps the sidebar still while the body changes — so this hook lives as
   * long as the tab does. Without this, opening the inbox would mark everything
   * seen on the server while the badge beside it went on saying seven until a
   * full page reload. It is one small GET per navigation, and the endpoint
   * marks nothing.
   */
  useEffect(() => {
    if (!enabled) return;

    let active = true;
    void fetchNotifications(inboxOndoId)
      .then((response) => {
        if (active) setUnseen(response.unseen);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [enabled, destination, inboxOndoId]);

  return enabled ? unseen : 0;
}

/**
 * How many settlements are waiting on the reader, for the badge on the rail.
 *
 * OUTSTANDING WORK, like the verifications badge, and not news: it stays until
 * the thing is done. The definition of "done" is the screen's own — the same
 * `settlementGroup` the gallery's Settlements groups by, and the same three
 * conditions the artist's `canAnswer` is built from — so the number on the rail
 * and the rows under the group heading cannot disagree.
 *
 * ONE ROLE CHECK, and it is addressing rather than authorization. The two
 * endpoints are the two sides of one ledger: `GET /v1/gallery/settlements` is
 * what a covenant holder is owed, `GET /v1/settlements/mine` is what an artist
 * is owed. Which side the reader is on is what the workspace's role says, and
 * asking either endpoint from the wrong side returns an honest empty list
 * rather than an error — so a wrong branch here would cost a missing badge, not
 * a wrong one. `hrefFor` in notifications.model.ts branches on the role for
 * the representation-request row for the same reason.
 *
 * Re-read on every move between destinations, as the inbox badge is: settling
 * a row and walking back to the rail should show one fewer.
 */
function useOutstandingSettlements(
  enabled: boolean,
  asGallery: boolean,
  destination: string,
): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    const read = asGallery
      ? fetchGallerySettlements().then(dueSettlementCount)
      : fetchArtistSettlements().then(unansweredRemittanceCount);

    void read
      .then((n) => {
        if (active) setCount(n);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [enabled, asGallery, destination]);

  return enabled ? count : 0;
}

/**
 * How many verifications are waiting, for the badge on the nav item.
 *
 * GATED ON THE CAPABILITY, which is exactly what it was added for — its doc
 * comment in `packages/shared/src/api/me.ts` says so outright. `verifyProvenance`
 * is already in the `/me` response every page has, so the common case — nothing
 * waiting — costs no request at all, and the one extra call happens only for an
 * account that has something to answer.
 *
 * A refusal or an empty inbox both mean no badge. Neither is an error worth
 * taking a whole workspace down for.
 */
function useAwaitingVerifications(enabled: boolean, inboxOndoId: string): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    void fetchVerificationRequests(inboxOndoId)
      .then((requests) => {
        if (active) setCount(awaitingVerificationCount(requests));
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [enabled, inboxOndoId]);

  // Derived rather than reset in the effect, the way `useMe` derives `loading`
  // from a mismatch: an account whose capability went false has nothing waiting,
  // whatever the last fetch happened to say.
  return enabled ? count : null;
}
